// dNews / Reminder
//
// Mode toggle at the top: "Use a template" (default) vs "Compose directly".
// Template mode: dropdown of saved dnews_templates rows. Picking one fills
// subject + body but stays editable so admins can tweak per-send. Compose
// mode: blank subject + body, same fields as the Newsletter compose surface.
//
// Recipient picker is identical to Newsletter (general / department /
// job_position / location / employee). Send Now / Schedule for later
// at the bottom.
//
// Query-string hand-off: navigating here as /dNews_Reminder?template=N
// pre-selects template N (used by the Templates page's "Use in Reminder"
// shortcut button).

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import {
    FaPaperPlane, FaClock, FaPaperclip, FaTrashAlt, FaUsers, FaBell,
} from "react-icons/fa";
import { apiFetch } from "./utils/api";
import DNewsSelect from "./utils/dNews_Select";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./dNews_Newsletter.css";    // reuse card/label/input/submit styles
import "./dNews_Reminder.css";

const DEFAULT_FROM = "connect@dolluzcorp.com";

const TARGET_OPTIONS = [
    { value: "general",       label: "General (All Employees)" },
    { value: "department",    label: "Department" },
    { value: "job_position",  label: "Job Position" },
    { value: "location",      label: "Location" },
    { value: "employee",      label: "Specific Employees" },
    { value: "contact",       label: "Contact (non-employee)" },
    { value: "contact_group", label: "Distribution Group" },
];

export default function DNewsReminder({ navSize }) {
    const navigate = useNavigate();
    const location = useLocation();

    // ── Sender (From) state ───────────────────────────────────────────────
    const [senders, setSenders] = useState([]);
    const [fromEmail, setFromEmail] = useState(DEFAULT_FROM);

    // ── Mode + template state ─────────────────────────────────────────────
    const [mode, setMode] = useState("template");          // "template" | "compose"
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState(null);    // numeric id of selected template

    // Label categorises the send; picking one gates the unsubscribe link.
    const [labels, setLabels] = useState([]);
    const [labelId, setLabelId] = useState(null);

    // ── Compose state ────────────────────────────────────────────────────
    const [subject, setSubject] = useState("");
    const [files,   setFiles]   = useState([]);

    // ── Recipient state ──────────────────────────────────────────────────
    const [targetType, setTargetType] = useState("");   // no default - admin must choose
    const [targetIds,  setTargetIds]  = useState([]);
    const [audience,    setAudience]    = useState(null);
    const [recipientCount, setRecipientCount] = useState(0);

    // ── Schedule state ───────────────────────────────────────────────────
    const [scheduleMode, setScheduleMode] = useState(false);
    const [scheduledAt,  setScheduledAt]  = useState("");

    // ── Cc / Bcc state - same shape as Newsletter, collapsed by default.
    const [includeUnsub, setIncludeUnsub] = useState(false);
    const [showCcBcc, setShowCcBcc] = useState(false);
    const [ccType,  setCcType]  = useState("");
    const [ccIds,   setCcIds]   = useState([]);
    const [bccType, setBccType] = useState("");
    const [bccIds,  setBccIds]  = useState([]);

    const [sending, setSending] = useState(false);

    const { quill, quillRef } = useQuill({
        theme: "snow",
        modules: {
            toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                [{ color: [] }, { background: [] }],
                ["link"],
                ["clean"],
            ],
        },
    });

    // ── Initial loads: audience + templates ──────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const [audRes, tplRes, lblRes] = await Promise.all([
                    apiFetch("/api/dnews/newsletter/audience-options", { credentials: "include" }),
                    apiFetch("/api/dnews/templates/list", { credentials: "include" }),
                    apiFetch("/api/dnews/labels/list", { credentials: "include" }),
                ]);
                if (audRes.status === 401 || audRes.status === 403) { navigate("/login"); return; }
                setAudience(await audRes.json());
                const tlist = await tplRes.json();
                setTemplates(Array.isArray(tlist) ? tlist : []);
                const llist = await lblRes.json().catch(() => []);
                setLabels(Array.isArray(llist) ? llist : []);

                // Honour ?template=N query string from Templates → Use in Reminder.
                const params = new URLSearchParams(location.search);
                const preId = params.get("template");
                if (preId) {
                    setMode("template");
                    setTemplateId(Number(preId));
                }
            } catch {
                navigate("/login");
            }
        })();
    }, [navigate, location.search]);

    // ── Senders for From dropdown ───────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch("/api/dnews/senders/allowed", { credentials: "include" });
                if (!res.ok) return;
                const data = await res.json();
                setSenders(Array.isArray(data) ? data : []);
            } catch { /* leave default */ }
        })();
    }, []);

    // ── Apply template selection: fetch its body and load into Quill ────
    useEffect(() => {
        if (mode !== "template" || !templateId || !quill) return;
        (async () => {
            try {
                const res = await apiFetch(`/api/dnews/templates/${templateId}`, { credentials: "include" });
                if (!res.ok) throw new Error("Failed to load template");
                const t = await res.json();
                setSubject(t.subject || "");
                quill.clipboard.dangerouslyPasteHTML(t.body_html || "");
            } catch (e) {
                dnAlert.error("Load failed", e.message);
            }
        })();
    }, [templateId, mode, quill]);

    // ── Recipient count live preview ─────────────────────────────────────
    useEffect(() => {
        const abort = new AbortController();
        (async () => {
            try {
                const res = await apiFetch("/api/dnews/newsletter/resolve-recipients", {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ target_type: targetType, ids: targetIds }),
                    signal: abort.signal,
                });
                if (!res.ok) { setRecipientCount(0); return; }
                const data = await res.json();
                setRecipientCount(data.count || 0);
            } catch { /* aborted - leave previous count */ }
        })();
        return () => abort.abort();
    }, [targetType, targetIds]);

    const secondaryOptions = (() => {
        if (!audience) return [];
        switch (targetType) {
            case "department":    return audience.departments  || [];
            case "job_position":  return audience.jobPositions || [];
            case "location":      return audience.locations    || [];
            case "employee":      return audience.employees    || [];
            case "contact":       return audience.contacts     || [];
            case "contact_group": return audience.groups       || [];
            default:              return [];
        }
    })();

    const onTargetTypeChange = (v) => { setTargetType(v); setTargetIds([]); };

    // Shared option resolver for Cc/Bcc secondary multi-selects.
    const optionsFor = (t) => {
        if (!audience) return [];
        switch (t) {
            case "department":    return audience.departments  || [];
            case "job_position":  return audience.jobPositions || [];
            case "location":      return audience.locations    || [];
            case "employee":      return audience.employees    || [];
            case "contact":       return audience.contacts     || [];
            case "contact_group": return audience.groups       || [];
            default:              return [];
        }
    };
    const TARGET_OPTIONS_OPTIONAL = [
        { value: "", label: "- None -" },
        ...TARGET_OPTIONS,
    ];

    const onPickFiles = (e) => {
        const picked = Array.from(e.target.files || []);
        const over = picked.find(f => f.size > 5 * 1024 * 1024);
        if (over) {
            dnAlert.warning("File too large", `"${over.name}" exceeds 5MB.`);
            e.target.value = "";
            return;
        }
        // At most 3 files, 15MB total (matches the backend + SendGrid's cap).
        if (files.length + picked.length > 3) {
            dnAlert.warning("Too many files", "You can attach at most 3 files.");
            e.target.value = "";
            return;
        }
        const total = [...files, ...picked].reduce((s, f) => s + f.size, 0);
        if (total > 15 * 1024 * 1024) {
            dnAlert.warning("Attachments too large", "Total attachment size must stay under 15MB.");
            e.target.value = "";
            return;
        }
        setFiles(prev => [...prev, ...picked]);
        e.target.value = "";
    };
    const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

    // ── Switching modes resets the body so a leftover template doesn't
    //    leak into a "compose from scratch" send. Subject is also cleared.
    const onModeChange = (newMode) => {
        if (newMode === mode) return;
        setMode(newMode);
        setTemplateId(null);
        setSubject("");
        if (quill) quill.setText("");
    };

    // ── Submit ───────────────────────────────────────────────────────────
    const validate = () => {
        if (mode === "template" && !templateId) return "Pick a template, or switch to Compose directly.";
        if (!targetType) return "Choose who to send to in the To field.";
        if (!subject.trim()) return "Subject is required.";
        const html = quill?.root?.innerHTML?.trim() || "";
        if (!html || html === "<p><br></p>") return "Body cannot be empty.";
        if (targetType !== "general" && targetIds.length === 0) {
            return `Pick at least one ${targetType.replace("_", " ")}.`;
        }
        if (recipientCount === 0) return "No recipients resolved for the selected audience.";
        if (scheduleMode) {
            if (!scheduledAt) return "Pick a date/time to schedule.";
            if (new Date(scheduledAt).getTime() < Date.now()) return "Scheduled time must be in the future.";
        }
        return null;
    };

    const onSubmit = async () => {
        const err = validate();
        if (err) { dnAlert.warning("Cannot send", err); return; }

        const action = scheduleMode ? "Schedule" : "Send";
        const tplName = templateId ? (templates.find(t => t.id === templateId)?.name || "-") : "-";
        const ok = await dnAlert.confirm({
            title: `${action} this reminder?`,
            html: `${mode === "template" ? `<p><strong>Template:</strong> ${escapeHtml(tplName)}</p>` : ""}
                   <p><strong>Subject:</strong> ${escapeHtml(subject)}</p>
                   <p><strong>Recipients:</strong> ${recipientCount} ${recipientCount === 1 ? "person" : "people"}</p>
                   <p><strong>From:</strong> ${fromEmail}</p>
                   ${scheduleMode ? `<p><strong>Scheduled for:</strong> ${new Date(scheduledAt).toLocaleString()}</p>` : `<p><strong>When:</strong> Immediately</p>`}`,
            confirmLabel: scheduleMode ? "Schedule" : "Send now",
        });
        if (!ok) return;

        const fd = new FormData();
        fd.append("target_type", targetType);
        fd.append("target_ids", JSON.stringify(targetIds));
        if (showCcBcc && ccType) {
            fd.append("cc_target_type", ccType);
            fd.append("cc_target_ids", JSON.stringify(ccIds));
        }
        if (showCcBcc && bccType) {
            fd.append("bcc_target_type", bccType);
            fd.append("bcc_target_ids", JSON.stringify(bccIds));
        }
        fd.append("from_email", fromEmail);
        fd.append("subject", subject);
        fd.append("body_html", quill.root.innerHTML);
        fd.append("include_unsubscribe", includeUnsub ? "1" : "0");
        if (labelId) fd.append("label_id", String(labelId));
        if (templateId) fd.append("template_id", String(templateId));
        if (scheduleMode) fd.append("scheduled_at", new Date(scheduledAt).toISOString());
        for (const f of files) fd.append("attachments", f);

        setSending(true);
        try {
            const endpoint = scheduleMode
                ? "/api/dnews/reminders/schedule"
                : "/api/dnews/reminders/send";
            const res = await apiFetch(endpoint, { method: "POST", credentials: "include", body: fd });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Send failed");

            if (scheduleMode) {
                dnAlert.success("Scheduled", `Reminder scheduled for ${new Date(data.scheduled_for).toLocaleString()} (${data.recipients} recipients).`);
            } else {
                const failedNote = data.failed > 0 ? ` ${data.failed} failed.` : "";
                const summary = `${data.sent} of ${data.sent + data.failed} delivered.${failedNote}`;
                if (data.failed === 0) dnAlert.success("Sent", summary);
                else                   dnAlert.warning("Sent with errors", summary);
            }

            // Reset for the next reminder (audience too - was sticky before).
            setSubject("");
            setFiles([]);
            if (quill) quill.setText("");
            setTemplateId(null);
            setScheduleMode(false);
            setScheduledAt("");
            setTargetType("general");
            setTargetIds([]);
            setShowCcBcc(false);
            setIncludeUnsub(false);
            setLabelId(null);
            setCcType(""); setCcIds([]);
            setBccType(""); setBccIds([]);
        } catch (e) {
            dnAlert.error("Send failed", e.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="dspr-container">
            <div className={`dspr-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></a>
                        dNews - Reminder
                    </h2>
                    <p className="dspr-subtitle">Pick a saved template or compose directly. Send to all employees, a department, a job position, a location, or specific people. Merge tokens like {"{employee_name}"} are substituted per recipient.</p>
                </div>

                <div className="dspr-dashboard-content">
                    <section className="dnews-card">
                        {/* Mode toggle */}
                        <div className="dnews-rem-mode">
                            <button
                                type="button"
                                className={`dnews-rem-mode-btn ${mode === "template" ? "active" : ""}`}
                                onClick={() => onModeChange("template")}
                            >
                                Use a template
                            </button>
                            <button
                                type="button"
                                className={`dnews-rem-mode-btn ${mode === "compose" ? "active" : ""}`}
                                onClick={() => onModeChange("compose")}
                            >
                                Compose directly
                            </button>
                        </div>

                        {/* From - whitelisted senders */}
                        <div className="dnews-row">
                            <label className="dnews-label dnews-label-req">From</label>
                            <DNewsSelect
                                value={fromEmail}
                                onChange={(v) => setFromEmail(v)}
                                options={(senders.length ? senders : [{ email: DEFAULT_FROM, label: DEFAULT_FROM }])
                                    .map(s => ({ value: s.email, label: s.label || s.email }))}
                            />
                        </div>

                        {/* Template picker - only when template mode */}
                        {mode === "template" && (
                            <div className="dnews-row">
                                <label className="dnews-label">Template</label>
                                <DNewsSelect
                                    value={templateId || ""}
                                    onChange={(v) => setTemplateId(v ? Number(v) : null)}
                                    placeholder="- Choose a template -"
                                    options={[
                                        { value: "", label: "- Choose a template -" },
                                        ...templates.map(t => ({ value: t.id, label: t.name })),
                                    ]}
                                />
                                {templates.length === 0 && (
                                    <p className="dnews-rem-no-templates">
                                        No templates yet. <a href="/dNews_Templates">Create one here →</a>
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Subject */}
                        <div className="dnews-row">
                            <label className="dnews-label dnews-label-req">Subject</label>
                            <input
                                type="text"
                                className="dnews-input"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder="Email subject - tokens like {employee_name} are supported"
                                maxLength={250}
                            />
                        </div>

                        {/* Body */}
                        <div className="dnews-row">
                            <label className="dnews-label dnews-label-req">Body</label>
                            <div className="dnews-quill-host">
                                <div ref={quillRef} />
                            </div>
                        </div>

                        {/* Recipients */}
                        <div className="dnews-row">
                            <label className="dnews-label dnews-label-req">To</label>
                            <DNewsSelect
                                value={targetType}
                                onChange={(v) => onTargetTypeChange(v)}
                                options={TARGET_OPTIONS}
                                placeholder="- Choose recipients -"
                            />
                        </div>

                        {targetType !== "general" && (
                            <div className="dnews-row">
                                <label className="dnews-label">Select {targetType.replace("_", " ")}</label>
                                <Select
                                    key={targetType}
                                    isMulti
                                    {...checkboxMultiProps}
                                    isLoading={!audience}
                                    options={secondaryOptions}
                                    value={secondaryOptions.filter(o => targetIds.includes(String(o.value)))}
                                    onChange={(sel) => setTargetIds(sel ? sel.map(s => String(s.value)) : [])}
                                    menuPortalTarget={document.body}
                                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                    placeholder={`Pick one or more ${targetType.replace("_", " ")}...`}
                                />
                            </div>
                        )}

                        {/* Cc / Bcc - same structure as Newsletter. */}
                        {!showCcBcc ? (
                            <button
                                type="button"
                                className="dnews-ccbcc-toggle"
                                onClick={() => setShowCcBcc(true)}
                            >
                                + Add Cc / Bcc
                            </button>
                        ) : (
                            <div className="dnews-ccbcc-block">
                                <div className="dnews-row">
                                    <label className="dnews-label">Cc (optional)</label>
                                    <DNewsSelect
                                        value={ccType}
                                        onChange={(v) => { setCcType(v); setCcIds([]); }}
                                        options={TARGET_OPTIONS_OPTIONAL}
                                    />
                                </div>
                                {ccType && ccType !== "general" && (
                                    <div className="dnews-row">
                                        <label className="dnews-label">Cc - Select {ccType.replace("_", " ")}</label>
                                        <Select
                                            key={`cc-${ccType}`}
                                            isMulti
                                            {...checkboxMultiProps}
                                            isLoading={!audience}
                                            options={optionsFor(ccType)}
                                            value={optionsFor(ccType).filter(o => ccIds.includes(String(o.value)))}
                                            onChange={(sel) => setCcIds(sel ? sel.map(s => String(s.value)) : [])}
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                            placeholder={`Pick one or more ${ccType.replace("_", " ")}...`}
                                        />
                                    </div>
                                )}
                                <div className="dnews-row">
                                    <label className="dnews-label">Bcc (optional)</label>
                                    <DNewsSelect
                                        value={bccType}
                                        onChange={(v) => { setBccType(v); setBccIds([]); }}
                                        options={TARGET_OPTIONS_OPTIONAL}
                                    />
                                </div>
                                {bccType && bccType !== "general" && (
                                    <div className="dnews-row">
                                        <label className="dnews-label">Bcc - Select {bccType.replace("_", " ")}</label>
                                        <Select
                                            key={`bcc-${bccType}`}
                                            isMulti
                                            {...checkboxMultiProps}
                                            isLoading={!audience}
                                            options={optionsFor(bccType)}
                                            value={optionsFor(bccType).filter(o => bccIds.includes(String(o.value)))}
                                            onChange={(sel) => setBccIds(sel ? sel.map(s => String(s.value)) : [])}
                                            menuPortalTarget={document.body}
                                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                            placeholder={`Pick one or more ${bccType.replace("_", " ")}...`}
                                        />
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className="dnews-ccbcc-hide"
                                    onClick={() => {
                                        setShowCcBcc(false);
                                        setCcType("");  setCcIds([]);
                                        setBccType(""); setBccIds([]);
                                    }}
                                >
                                    − Hide Cc / Bcc
                                </button>
                            </div>
                        )}

                        <div className="dnews-recipient-count">
                            <FaUsers /> ≈{recipientCount} {recipientCount === 1 ? "recipient" : "recipients"}
                        </div>

                        {/* Attachments */}
                        <div className="dnews-row">
                            <label className="dnews-label">Attachments (optional)</label>
                            <div className="dnews-attach-zone">
                                <label className="dnews-attach-btn">
                                    <FaPaperclip /> Add files
                                    <input type="file" multiple onChange={onPickFiles} hidden />
                                </label>
                                <span className="dnews-attach-hint">Max 5MB per file · up to 3 files</span>
                            </div>
                            {files.length > 0 && (
                                <ul className="dnews-attach-list">
                                    {files.map((f, i) => (
                                        <li key={i}>
                                            <FaPaperclip className="dnews-attach-icon" />
                                            <span className="dnews-attach-name">{f.name}</span>
                                            <span className="dnews-attach-size">{(f.size / 1024).toFixed(1)} KB</span>
                                            <button type="button" className="dnews-attach-remove" onClick={() => removeFile(i)}>
                                                <FaTrashAlt />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Label - categorises the send. Picking one auto-enables the
                            unsubscribe link (recipients opt out of THIS label only). */}
                        <div className="dnews-row">
                            <label className="dnews-label">Label (optional)</label>
                            <DNewsSelect
                                value={labelId ?? ""}
                                onChange={(v) => {
                                    const id = v ? Number(v) : null;
                                    setLabelId(id);
                                    setIncludeUnsub(!!id);   // auto-check on select, uncheck on clear
                                }}
                                options={[{ value: "", label: "No label" },
                                          ...labels.filter(l => l.active !== 0).map(l => ({ value: l.id, label: l.name }))]}
                                placeholder="Pick a label…"
                            />
                        </div>

                        {/* Unsubscribe link - only available once a label is chosen; the
                            footer opts the person out of that label's mail (not all dNews). */}
                        <div className="dnews-schedule-row">
                            <label className="dnews-schedule-check" style={!labelId ? { opacity: .5 } : undefined}>
                                <input
                                    type="checkbox"
                                    checked={includeUnsub}
                                    disabled={!labelId}
                                    onChange={(e) => setIncludeUnsub(e.target.checked)}
                                />
                                <FaUsers /> Include an “Unsubscribe” link in this email
                                {!labelId && <span style={{ color: "#94a3b8", marginLeft: 6 }}>(pick a label first)</span>}
                            </label>
                        </div>

                        {/* Schedule toggle */}
                        <div className="dnews-schedule-row">
                            <label className="dnews-schedule-check">
                                <input
                                    type="checkbox"
                                    checked={scheduleMode}
                                    onChange={(e) => setScheduleMode(e.target.checked)}
                                />
                                <FaClock /> Schedule for later
                            </label>
                            {scheduleMode && (
                                <input
                                    type="datetime-local"
                                    className="dnews-input dnews-schedule-input"
                                    value={scheduledAt}
                                    onChange={(e) => setScheduledAt(e.target.value)}
                                />
                            )}
                        </div>

                        {/* Submit */}
                        <div className="dnews-submit-row">
                            <button
                                type="button"
                                className="dnews-submit-btn"
                                disabled={sending}
                                onClick={onSubmit}
                            >
                                {sending
                                    ? <><span className="dn-spinner" /> Sending…</>
                                    : scheduleMode
                                        ? <><FaClock /> Schedule</>
                                        : <><FaBell /> Send reminder</>}
                            </button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

function escapeHtml(s) {
    return String(s || "")
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
